import { useEffect, useState } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { useAuth } from "@/features/auth/useAuth";
import {
  listMyDocuments,
  type DocumentKind,
  type DocumentRecord,
  type DocumentStatus,
} from "@/features/documents/repo";

const statusBadge: Record<DocumentStatus, "default" | "warning" | "success" | "danger"> = {
  new: "warning",
  in_review: "default",
  approved: "success",
  rejected: "danger",
};

const statusLabel: Record<DocumentStatus, string> = {
  new: "New",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
};

const kindLabel: Record<DocumentKind, string> = {
  bol: "BOL",
  pod: "POD",
  cdl: "CDL",
  medical_card: "Medical card",
  insurance: "Insurance",
  w9: "W-9",
  resume: "Resume",
  other: "Other",
};

export function MyDocumentsCard({ refreshKey = 0 }: { refreshKey?: number }) {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    if (!user) return;
    setLoading(true);
    try {
      setDocuments(await listMyDocuments(user.id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, refreshKey]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">My submitted documents</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => void refresh()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <FeedbackMessage tone="error">
            {error}
          </FeedbackMessage>
        )}

        {loading ? (
          <div className="grid gap-3">
            <CardSkeleton rows={2} />
            <CardSkeleton rows={2} />
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-brand-500">
            No documents submitted yet. Uploaded files will appear here after submission.
          </p>
        ) : (
          <div className="space-y-3">
            {documents.map((document) => (
              <div
                key={document.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-brand-100 p-3"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span className="mt-0.5 rounded-md bg-brand-50 p-2 text-brand-500">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="break-words font-medium text-brand-900">
                      {kindLabel[document.kind]}
                      {document.relatedReference ? ` - ${document.relatedReference}` : ""}
                    </p>
                    <p className="mt-0.5 text-xs text-brand-400">
                      {new Date(document.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Badge variant={statusBadge[document.status]}>
                  {statusLabel[document.status]}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
